import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { StrapiResponse, StrapiEntry, StrapiMediaField } from '../models/strapi.types';

const CMS_TIMEOUT_MS = 30000; // 30s — debugging timeout lento

@Injectable({ providedIn: 'root' })
export class StrapiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.strapiUrl;
  private apimaz = environment.apimaz;

  constructor() {}

  /** Retorna headers con APIMAZ subscription key */
  private getHeaders(): HttpHeaders {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    if (this.apimaz?.subscriptionKey && this.apimaz?.headerKey) {
      return headers.set(this.apimaz.headerKey, this.apimaz.subscriptionKey);
    }

    return headers;
  }

  /** Resuelve la URL pública de un media field de Strapi */
  getMediaUrl(media: StrapiMediaField): string | null {
    const url = media?.data?.attributes?.url;
    if (!url) return null;
    return url.startsWith('http') ? url : `${this.baseUrl}${url}`;
  }

  /** GET colección paginada — los errores se PROPAGAN para que cada service maneje su propio fallback */
  getCollection<T>(
    endpoint: string,
    params: Record<string, string | number | boolean> = {}
  ): Observable<StrapiResponse<StrapiEntry<T>[]>> {
    let httpParams = new HttpParams();
    for (const [key, value] of Object.entries(params)) {
      httpParams = httpParams.set(key, String(value));
    }
    return this.http
      .get<StrapiResponse<StrapiEntry<T>[]>>(`${this.baseUrl}/api/${endpoint}`, {
        params: httpParams,
        headers: this.getHeaders()
      })
      .pipe(
        timeout(CMS_TIMEOUT_MS),
        // No catchError aquí — el error se propaga al service consumidor
      );
  }

  /** GET single entry by id o slug — los errores se PROPAGAN */
  getOne<T>(endpoint: string, idOrSlug: string | number, params: Record<string, string> = {}): Observable<StrapiEntry<T> | null> {
    let httpParams = new HttpParams();
    for (const [key, value] of Object.entries(params)) {
      httpParams = httpParams.set(key, value);
    }
    return this.http
      .get<StrapiResponse<StrapiEntry<T>>>(`${this.baseUrl}/api/${endpoint}/${idOrSlug}`, {
        params: httpParams,
        headers: this.getHeaders()
      })
      .pipe(
        timeout(CMS_TIMEOUT_MS),
        map((res) => res.data),
        // No catchError aquí — el error se propaga al service consumidor
      );
  }

  /** GET single type — los errores se PROPAGAN */
  getSingle<T>(endpoint: string, params: Record<string, string> = {}): Observable<StrapiEntry<T> | null> {
    let httpParams = new HttpParams();
    for (const [key, value] of Object.entries(params)) {
      httpParams = httpParams.set(key, value);
    }
    return this.http
      .get<StrapiResponse<StrapiEntry<T>>>(`${this.baseUrl}/api/${endpoint}`, {
        params: httpParams,
        headers: this.getHeaders()
      })
      .pipe(
        timeout(CMS_TIMEOUT_MS),
        map((res) => res.data),
        // No catchError aquí — el error se propaga al service consumidor
      );
  }

  /** POST — para formularios (contacto, cotización, agendamiento) */
  post<T>(endpoint: string, body: unknown): Observable<StrapiResponse<StrapiEntry<T>> | null> {
    return this.http
      .post<StrapiResponse<StrapiEntry<T>>>(`${this.baseUrl}/api/${endpoint}`, { data: body }, {
        headers: this.getHeaders()
      })
      .pipe(
        catchError((err) => {
          console.error(`[Strapi] Error POST /${endpoint}:`, err.message ?? err);
          return of(null);
        })
      );
  }
}
